import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Check, Sparkles } from 'lucide-react';

const plans = [
  { key: 'p1', price: '$400', featureCount: 5, highlighted: false },
  { key: 'p2', price: '$800', featureCount: 6, highlighted: true },
  { key: 'p3', price: '$1,500', featureCount: 7, highlighted: false },
];

const addonKeys = ['a1', 'a2', 'a3'];

const Pricing = () => {
  const { t } = useTranslation();
  const scrollToContact = () => document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });

  return (
    <section id="pricing" data-testid="pricing-section" className="py-14 lg:py-20 bg-[#F5EDE4] relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none select-none overflow-hidden z-0">
        <motion.div className="absolute whitespace-nowrap" style={{ bottom: '5%' }}
          animate={{ x: ['0%', '-30%'] }} transition={{ duration: 32, repeat: Infinity, ease: 'linear' }}>
          <span className="text-[16vw] font-bold text-[#1A1816]/[0.025] tracking-[-0.05em] font-sans leading-none">
            PRICING&nbsp;&nbsp;&nbsp;VALUE&nbsp;&nbsp;&nbsp;PRICING&nbsp;&nbsp;&nbsp;VALUE
          </span>
        </motion.div>
      </div>
      <div className="absolute top-20 left-20 w-64 h-64 bg-[#C9A96E]/10 rounded-full blur-3xl" />
      <div className="absolute bottom-20 right-20 w-96 h-96 bg-[#C9A96E]/5 rounded-full blur-3xl" />

      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 relative z-10">
        <motion.div className="mb-16" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-[1px] bg-[#C9A96E]" />
            <span className="text-[9px] font-semibold tracking-[0.3em] uppercase text-[#8A857D]">{t('pricing.label')}</span>
          </div>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-sans font-bold text-[#1A1816] leading-tight">
            {t('pricing.title1')} <span className="font-serif italic text-[#C9A96E]">{t('pricing.title2')}</span>
          </h2>
          <p className="mt-4 text-sm sm:text-base text-[#8A857D] max-w-lg">{t('pricing.sub')}</p>
        </motion.div>

        <h3 className="text-xs font-semibold tracking-[0.25em] uppercase text-[#8A857D] mb-6">{t('pricing.packages')}</h3>
        <div className="grid lg:grid-cols-3 gap-6 lg:gap-0 mb-16">
          {plans.map((plan, index) => {
            const features = Array.from({ length: plan.featureCount }, (_, i) => t(`pricing.${plan.key}_f${i + 1}`));
            return (
              <motion.div
                key={plan.key}
                data-testid={`pricing-${t(`pricing.${plan.key}_name`).toLowerCase()}`}
                className={`relative ${plan.highlighted ? 'bg-[#1A1816] text-[#FAF8F5] lg:-my-4 z-10' : 'bg-[#FAF8F5] border border-[#1A1816]/10'}`}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                {plan.highlighted && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 flex items-center gap-2 bg-[#C9A96E] text-[#1A1816] px-5 py-1.5 text-[10px] font-semibold tracking-wider uppercase whitespace-nowrap">
                    <Sparkles className="w-3 h-3" /> {t('pricing.popular')}
                  </div>
                )}
                <div className="p-8 lg:p-10">
                  <h4 className={`text-xs font-semibold tracking-[0.2em] uppercase ${plan.highlighted ? 'text-[#C9A96E]' : 'text-[#8A857D]'}`}>{t(`pricing.${plan.key}_name`)}</h4>
                  <div className="mt-4 mb-2">
                    <span className="block text-[10px] tracking-wider uppercase text-[#8A857D]/60 mb-1">{t('pricing.from')}</span>
                    <span className={`text-5xl lg:text-6xl font-serif italic ${plan.highlighted ? 'text-[#C9A96E]' : 'text-[#1A1816]'}`}>{plan.price}</span>
                  </div>
                  <p className={`text-sm mb-8 ${plan.highlighted ? 'text-[#FAF8F5]/60' : 'text-[#8A857D]'}`}>{t(`pricing.${plan.key}_desc`)}</p>
                  <ul className="space-y-3 mb-8">
                    {features.map((f) => (
                      <li key={f} className="flex items-start gap-3">
                        <Check className="w-4 h-4 flex-shrink-0 mt-0.5 text-[#C9A96E]" />
                        <span className={`text-sm ${plan.highlighted ? 'text-[#FAF8F5]/80' : 'text-[#1A1816]/70'}`}>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <button onClick={scrollToContact}
                    data-testid={`pricing-${plan.key}-cta`}
                    className={`w-full py-3.5 font-semibold text-xs tracking-wider uppercase transition-all duration-500 ${
                      plan.highlighted ? 'bg-[#C9A96E] text-[#1A1816] hover:bg-[#FAF8F5]' : 'bg-[#1A1816] text-[#FAF8F5] hover:bg-[#C9A96E] hover:text-[#1A1816]'
                    }`}>
                    {t('pricing.cta')}
                  </button>
                </div>
              </motion.div>
            );
          })}
        </div>

        <h3 className="text-xs font-semibold tracking-[0.25em] uppercase text-[#8A857D] mb-6">{t('pricing.addons')}</h3>
        <div className="grid sm:grid-cols-3 gap-6">
          {addonKeys.map((ak, i) => (
            <motion.div
              key={ak}
              data-testid={`addon-${i}`}
              className="p-6 bg-[#FAF8F5] border border-[#1A1816]/5"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
            >
              <h4 className="text-sm font-semibold text-[#1A1816] mb-1">{t(`pricing.${ak}_name`)}</h4>
              <p className="text-lg font-serif italic text-[#C9A96E] mb-2">{t(`pricing.${ak}_price`)}</p>
              <p className="text-xs text-[#8A857D]">{t(`pricing.${ak}_desc`)}</p>
            </motion.div>
          ))}
        </div>

        <motion.p className="mt-12 text-center text-sm text-[#8A857D]" initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: 0.5 }}>
          {t('pricing.guarantee')}
        </motion.p>
      </div>
    </section>
  );
};

export default Pricing;
